import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { META } from "../lib/store";
import {
  RadioTower,
  LayoutDashboard,
  GraduationCap as CapIcon,
  CalendarClock,
  BookOpen,
  GraduationCap,
  Layers,
  ListChecks,
  Star,
  BookMarked,
  Radio,
  Settings,
  MoreHorizontal,
  LogIn,
  LogOut,
  Moon,
  Sun,
} from "lucide-react";

const PRIMARY = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, end: true },
  { to: "/formation", label: "Formation", icon: CapIcon, highlight: true },
  { to: "/reviser", label: "Réviser", icon: CalendarClock },
  { to: "/etudier", label: "Étudier", icon: BookOpen },
  { to: "/examen", label: "Examen blanc", icon: GraduationCap },
];
const MORE = [
  { to: "/quiz", label: "Quiz par thème", icon: ListChecks },
  { to: "/cartes", label: "Cartes mémoires", icon: Layers },
  { to: "/favoris", label: "Favoris", icon: Star },
  { to: "/glossaire", label: "Glossaire", icon: BookMarked },
  { to: "/qcodes", label: "Codes Q", icon: Radio },
  { to: "/reglages", label: "Réglages", icon: Settings },
];
const ALL = [...PRIMARY, ...MORE];

function AuthControl() {
  const { user, loading, login, logout } = useAuth();
  const [open, setOpen] = useState(false);
  if (loading) return <div className="w-9 h-9 rounded-sm bg-secondary animate-pulse" />;
  if (!user) {
    return (
      <button
        onClick={login}
        data-testid="login-btn"
        className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-3 py-2 text-sm font-semibold hover:-translate-y-px transition-transform"
      >
        <LogIn size={16} /> <span className="hidden sm:inline">Se connecter</span>
      </button>
    );
  }
  const initials = (user.name || user.email || "?").trim().charAt(0).toUpperCase();
  return (
    <div className="relative" onMouseLeave={() => setOpen(false)}>
      <button
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setOpen(true)}
        data-testid="user-menu"
        className="grid place-items-center w-9 h-9 rounded-sm overflow-hidden border border-border bg-secondary text-foreground"
      >
        {user.picture ? (
          <img src={user.picture} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
        ) : (
          <span className="font-display font-bold text-sm">{initials}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-60 rounded-sm border border-border bg-popover shadow-lg p-1 z-50" data-testid="user-menu-panel">
          <div className="px-3 py-2 border-b border-border mb-1">
            <div className="font-semibold text-sm truncate">{user.name || "Utilisateur"}</div>
            <div className="text-xs text-muted-foreground truncate">{user.email}</div>
            <div className="text-[11px] text-success mt-1">Progression synchronisée ✓</div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-sm text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
          >
            <LogOut size={16} /> Se déconnecter
          </button>
        </div>
      )}
    </div>
  );
}

export function Layout({ children }) {
  const { level, setLevel, theme, toggleTheme } = useApp();
  const loc = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  const linkClass = ({ isActive }) =>
    `flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium whitespace-nowrap transition-colors ${
      isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
    }`;

  return (
    <div data-level={level} className="min-h-screen bg-background text-foreground grain">
      <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur-xl print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center gap-3">
          <NavLink to="/" data-testid="brand-logo" className="flex items-center gap-2.5 shrink-0">
            <span className="grid place-items-center w-9 h-9 rounded-sm bg-primary text-primary-foreground">
              <RadioTower size={20} strokeWidth={2.4} />
            </span>
            <span className="hidden sm:flex flex-col leading-none">
              <span className="font-display font-black text-lg tracking-tight">HamPrep</span>
              <span className="font-mono text-[10px] text-muted-foreground tracking-widest">CANADA</span>
            </span>
          </NavLink>

          <nav className="hidden md:flex items-center gap-1 ml-2">
            {PRIMARY.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.to === "/" ? "dashboard" : n.to.slice(1)}`}
                className={({ isActive }) =>
                  `${linkClass({ isActive })} ${n.highlight && !isActive ? "ring-1 ring-primary/50 bg-primary/5 text-foreground" : ""}`
                }
              >
                <n.icon size={16} /> {n.label}
                {n.highlight && (
                  <span className="ml-1 rounded-sm bg-primary/20 text-primary text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5">
                    Départ
                  </span>
                )}
              </NavLink>
            ))}
            <div className="relative" onMouseLeave={() => setMoreOpen(false)}>
              <button
                onClick={() => setMoreOpen(true)}
                onMouseEnter={() => setMoreOpen(true)}
                data-testid="nav-more"
                className="flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
              >
                <MoreHorizontal size={16} /> Plus
              </button>
              {moreOpen && (
                <div className="absolute right-0 top-full mt-1 w-52 rounded-sm border border-border bg-popover shadow-lg p-1 z-50" data-testid="nav-more-menu">
                  {MORE.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      onClick={() => setMoreOpen(false)}
                      data-testid={`nav-${n.to.slice(1)}`}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 px-3 py-2 rounded-sm text-sm transition-colors ${
                          isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                        }`
                      }
                    >
                      <n.icon size={16} /> {n.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <span className="hidden lg:inline text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Niveau</span>
            <div className="flex rounded-sm border-2 border-primary/60 overflow-hidden" data-testid="level-switcher">
              {Object.keys(META).map((lv) => (
                <button
                  key={lv}
                  onClick={() => setLevel(lv)}
                  data-testid={`level-${lv}`}
                  className={`px-3 py-1.5 text-xs font-mono font-bold uppercase tracking-wider transition-colors ${
                    level === lv ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {META[lv].label}
                </button>
              ))}
            </div>
            <button onClick={toggleTheme} data-testid="theme-toggle" className="grid place-items-center w-9 h-9 rounded-sm border border-border text-muted-foreground hover:text-foreground transition-colors">
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <AuthControl />
          </div>
        </div>
        <div className="h-[3px] bg-primary" data-testid="level-accent-strip" />

        {/* mobile nav */}
        <nav className="md:hidden flex overflow-x-auto border-t border-border">
          {ALL.map((n) => {
            const active = n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
            return (
              <NavLink key={n.to} to={n.to} end={n.end} className={`flex-1 min-w-[68px] flex flex-col items-center gap-1 py-2 text-[10px] font-medium ${active ? "text-primary" : "text-muted-foreground"}`}>
                <n.icon size={18} /> {n.label.split(" ")[0]}
              </NavLink>
            );
          })}
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-12">{children}</main>
    </div>
  );
}
